import { useEffect, useState } from 'react';
import { resolveAvatarDisplay } from '@teo/widget-shared';
import { useWidget } from '../context/WidgetContext.js';
import { whenAvatarReady } from '../utils/avatar-image.js';

type AvatarProps = {
  size?: 'sm' | 'md';
  className?: string;
};

type AvatarImageProps = {
  src: string;
  className?: string;
  onError?: () => void;
};

/**
 * Renders an avatar photo only after full decode — no progressive stripe paint.
 */
export function AvatarImage({ src, className = '', onError }: AvatarImageProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);

    void whenAvatarReady(src).then((ok) => {
      if (cancelled) return;
      if (ok) {
        setReady(true);
      } else {
        onError?.();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [src, onError]);

  return (
    <img
      className={`teo-avatar-img${ready ? ' teo-avatar-img--ready' : ''}${className ? ` ${className}` : ''}`}
      src={src}
      alt=""
      decoding="async"
      draggable={false}
    />
  );
}

export function BotIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7v1h-2v-1a5 5 0 0 0-5-5h-1v1.27c.6.34 1 .99 1 1.73a2 2 0 1 1-4 0c0-.74.4-1.39 1-1.73V9H9a5 5 0 0 0-5 5v1H2v-1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 12 2zm-3 13h6v6h-2v-4h-2v4H9v-6z" />
    </svg>
  );
}

export function Avatar({
  size = 'md',
  className = '',
}: AvatarProps) {
  const { settings } = useWidget();
  const settingsName = settings.content.aiAgentName;
  const display = resolveAvatarDisplay(
    settings.content.aiAvatarUrl,
    settingsName,
  );

  const sizeClass = size === 'sm' ? 'teo-avatar--sm' : '';

  if (display.type === 'image') {
    return (
      <div className={`teo-avatar ${sizeClass} ${className}`.trim()}>
        <AvatarImage src={display.url} />
      </div>
    );
  }

  if (display.type === 'label') {
    return (
      <div
        className={`teo-avatar ${sizeClass} ${className}`.trim()}
        title={settingsName}
      >
        <span className="teo-avatar__label">{display.text}</span>
      </div>
    );
  }

  return (
    <div className={`teo-avatar ${sizeClass} ${className}`.trim()}>
      <BotIcon />
    </div>
  );
}
